import { Container, Filter } from "pixi.js";
import { OutlineFilter, OutlineFilterOptions } from "pixi-filters/outline";
import { Entity } from "../core/entity/Entity";

/**
 * An outline filter that rasterizes at the canvas's own resolution. A
 * filter renders its target into an intermediate texture, and PIXI's
 * default pins that texture at 1x — on a high-density display the
 * filtered object came back CSS-stretched and blurry the moment the
 * white rim appeared. "inherit" keeps the texture at the render
 * target's resolution instead.
 *
 * Thickness stays in CSS pixels once it does. The shader steps its ring
 * in UV, and OutlineFilter sets that step to `thickness / source.width`
 * — where PIXI's `width` is the texture's logical size, not its pixel
 * size — so the step already carries the texture's resolution and the
 * rim comes out the same weight on any display. Scaling thickness by
 * the resolution as well counted it twice, and every rim in the game
 * drew double-thick on a 2x screen.
 */
function crispOutline(
  options: OutlineFilterOptions & { thickness: number },
): OutlineFilter {
  const filter = new OutlineFilter(options);
  filter.resolution = "inherit";
  return filter;
}

/**
 * The in-world targeting treatment, shared by everything the keyboard is
 * about to act on: the machine the player stands at and the pile E would
 * pick up wear the same white rim, so "this is the target" reads the same
 * for stations and stock. An outline shader hugging the silhouette rather
 * than a box around the footprint — the white line carries a soft dark
 * halo outside it to stay readable over any floor or art.
 *
 * One shared array: pixi filters hold no per-object state, so the same
 * instances can dress every highlighted object in a frame.
 */
export const TARGET_HIGHLIGHT_FILTERS: Filter[] = [
  crispOutline({
    thickness: 2.5,
    color: 0xffffff,
    alpha: 0.9,
    quality: 0.5,
  }),
  crispOutline({
    thickness: 2,
    color: 0x1c1917,
    alpha: 0.35,
    quality: 0.5,
  }),
];

/**
 * The rim above, with the thing itself knocked out: the line and its halo
 * alone, on a clear field. Dressing a second copy of the target, drawn in
 * a layer over everything else, puts the rim on top of whatever lies
 * across the target — a piece on the bench shows its outline through the
 * clamps holding it and the boards stacked on it.
 *
 * Two layers, drawn in this order, rather than the one chain above. A
 * knockout clears the middle, so a halo chained after it would find the
 * inside edge of the line as well and draw a second dark contour over
 * the target's own face. Instead the halo is knocked out from the
 * silhouette on its own, reaching far enough to clear the line (2.5 +
 * 2), and the line is laid over it — leaving dark only outside, which is
 * where the rim on a target with nothing over it has always put it.
 */
export const TARGET_RIM_LAYERS: ReadonlyArray<Filter[]> = [
  [
    crispOutline({
      thickness: 4.5,
      color: 0x1c1917,
      alpha: 0.35,
      quality: 0.5,
      knockout: true,
    }),
  ],
  [
    crispOutline({
      thickness: 2.5,
      color: 0xffffff,
      alpha: 0.9,
      quality: 0.5,
      knockout: true,
    }),
  ],
];

/**
 * What the guided opening points at (see game/tutorial.ts). Deliberately
 * a different color from the white targeting rim: white means "the keys
 * act on this, here, now", and orange means "go to this next" — a thing
 * you usually aren't standing at yet. When both apply the white rim wins,
 * because by then the tutorial's arrow has done its job.
 */
export const TUTORIAL_HIGHLIGHT_FILTERS: Filter[] = [
  crispOutline({
    thickness: 2.5,
    color: 0xf59e0b,
    alpha: 0.9,
    quality: 0.5,
  }),
  crispOutline({
    thickness: 2,
    color: 0x1c1917,
    alpha: 0.35,
    quality: 0.5,
  }),
];

/**
 * A view that draws more than one thing, naming the one a rim means. A
 * machine parks its cast shadow and its cut spray beside the machine
 * itself, and the truck's cargo rides beside its body; rimming the first
 * drawable it happens to hold would trace the shadow.
 */
export interface HasHighlightRoot {
  readonly highlightRoot: Container;
}

function hasHighlightRoot(value: object): value is HasHighlightRoot {
  return "highlightRoot" in value;
}

/**
 * The container a rim dresses for a sim entity: the drawable its view
 * names, or its one sprite. Shared by both rims so the white targeting
 * outline and the tutorial's orange always land on the same thing.
 */
export function viewHighlightRoot(entity: Entity): Container | null {
  for (const child of entity.children ?? []) {
    if (hasHighlightRoot(child)) return child.highlightRoot;
    if (child.sprite) return child.sprite;
    if (child.sprites?.length) return child.sprites[0];
  }
  return null;
}

/** The view of a given class drawing a sim entity, for the rims that
 * dress a part of it — a piece on the stand, a piece in the bed. */
export function findChildView<T>(
  entity: Entity,
  viewClass: abstract new (...args: never[]) => T,
): T | null {
  for (const child of entity.children ?? []) {
    if (child instanceof viewClass) return child;
  }
  return null;
}
